import RupIcons from "../../RupIcons";
import SearchBox from "./components/SearchBox";
import SearchBoxPresentation from "./components/SearchBoxPresentation";
import SearchOptions from "./components/SearchOptions";
import SearchSwitcher from "./components/SearchSwitcher";
import Utils from "./components/common/Utils";

import GlobalSearchState, {
	ContentSearchBlock,
	GlobalSearchHandlers,
	GlobalSearchMode,
	GlobalSearchScopeName,
	KeyCode,
	MultiSearch,
	SearchBlockType
} from "./typings";

export interface IGlobalSearch extends Sys.Component {
	getSearchParams(): string;
	getSideItemsSelectedValue(): MultiSearch;
}

export default class GlobalSearch extends Sys.UI.Control implements IGlobalSearch {

	private state: GlobalSearchState;
	private container: JQuery;
	private searchContainer: JQuery;
	private searchForm: JQuery;
	private searchControl: JQuery;
	private searchBox: SearchBox;
	private clearButton: JQuery;
	private searchOptionsContainer: JQuery;
	private searchOptions: SearchOptions;
	private searchSwitcher: SearchSwitcher;
	private hideSearchOptions = false;

	private lookupTimer: number;

	private _isPresentationMode = false;

	public setEventHandlers(handlers: GlobalSearchHandlers): void {
		this.state.handlers = handlers;
	}

	public get searchMode(): GlobalSearchMode {
		return this.state.searchMode;
	}

	public setSearchMode(value: GlobalSearchMode): void {
		this.state.searchMode = value;
	}

	public getSearchParams(): string {
		return this.searchBox.searchParams;
	}

	public getSearchBlocks(): ContentSearchBlock[] {
		if (!this.searchBox.terms.length) {
			return this.searchBox.text ? [this.searchBox.defaultTextBlock] : [];
		}
		return this.searchBox.terms;
	}

	public getSideItemsSelectedValue(): MultiSearch {
		return this.searchSwitcher?.selectedItem || this.state.selectedSideItemValue;
	}

	public getQueryLabel(): string {
		return this.getSearchBlocks()
			.map(block => block.text)
			.join(" ")
			.replace(/\( /g, '(')
			.replace(/ \)/g, ')');
	}

	private get isPresentationMode() {
		return this._isPresentationMode && this.state.searchMode != GlobalSearchMode.Default;
	}

	private set isPresentationMode(value: boolean) {
		this._isPresentationMode = value;
	}

	constructor(element: HTMLElement) {
		super(element);
		this.container = $(this.get_element());
		this.focusOutControl = this.focusOutControl.bind(this);
		this.getSearchParams = this.getSearchParams.bind(this);
	}

	public initialize(): void {
		this.state = JSON.parse(String(this.state));
		this.state.contentSearchBlockList?.forEach?.(block => {
			const vocab = this.state.vocabularies?.filter(vocab => vocab.Id == block.descriptor.vocabularyId)[0];
			block.vocab = vocab ? vocab.displayName ?? vocab.name : "";
			const descriptor = this.state.descriptors.filter(descriptor => descriptor.id == block.descriptor.descriptorId)[0];
			block.descriptorName = descriptor ? descriptor.name : "";

			if ((block.descriptor.descriptorId != 0 && !descriptor) || (block.descriptor.vocabularyId != 0 && !vocab)) {
				block.invalid = true;
			}
		});

		this.isPresentationMode = this.state.contentSearchBlockList?.length > 0;
		this.state.selectedSideItemValue = this.state.selectedSideItemValue ?? MultiSearch.CCC;
		this.hideSearchOptions = this.state.isAdvancedSearchMode && (this.state.searchMode === GlobalSearchMode.Default);

		this.render();
		$(window).resize(() => this.hideOverflow());
		this.hideOverflow();
	}

	private hideOverflow() {
		if (this.isPresentationMode) {
			(this.searchBox as SearchBoxPresentation).hideOverflow();
		}
	}

	public render(): void {
		this.container
			.addClass("rup-global-search globalSearch")
			.append(this.renderSearchContainer())
			.attr({
				"aria-label": Labels.GlobalSearch,
				role: "search"
			});
	}

	private initializeSearchOptions(): void {
		this.searchOptions = new SearchOptions({
			state: this.state,
			searchBox: this.searchBox,
			onClick: (term: ContentSearchBlock) => {
				this.searchBox.selectedOption = null;
				this.searchBox.addChip(term);
				if (this.searchBox.isInInsertMode) {
					this.searchBox.insertModeOff();
				}
				if (this.state.searchMode === GlobalSearchMode.Default && 
					this.getSearchBlocks().length === 1) {
					this.searchBox.inputField.val(term.text);
					this.search();
					return;
				}
				this.searchBox.restoreLostPlaceholderFocus();
			}
		});

		this.searchOptionsContainer = this.searchOptions.render();
		this.searchContainer.append(this.searchOptionsContainer);
	}

	private renderSearchContainer(): JQuery {
		return this.searchContainer = $("<div>")
			.addClass("rup-global-search__container")
			.toggleClass("rup-global-search__container--active", this.isPresentationMode)
			.append(this.renderControl())
			.click((event) => {
				this.clickContainer(event);
			});
	}
	
	private clickContainer(event: JQueryEventObject) {
		event.preventDefault();
		this.toggleClearButton();
		if (this.isSearchControlChild(event.target)) {
			if (this.isPresentationMode && this.getSearchBlocks().length) {
				this.isPresentationMode = false;
				this.rerenderControl();
				this.searchBox.inputField.focus();
			}

			if (!this.searchOptions && !this.hideSearchOptions) {
				this.initializeSearchOptions();
				this.updateSearchOptions();
			}

			if (!this.isPresentationMode) {
				this.getDocuments().on("click", this.focusOutControl);
				this.searchContainer.addClass("rup-global-search__container--expanded rup-elevation");
			}
		}
	}

	private rerenderControl() {
		this.searchForm.remove();
		this.searchContainer.prepend(this.renderControl());
		if (this.isPresentationMode) {
			(this.searchBox as SearchBoxPresentation).hideOverflow();
		}
	}

	private isSearchControlChild(element: Element) {
		return $(element).closest(this.container).length;
	}

	private renderControl(): JQuery {
		this.clearButton = this.renderClearButton();
		this.searchBox = this.isPresentationMode ? this.getSearchBoxPresentation() : this.getSearchBox();
		const searchBox = this.searchBox.render();

		this.toggleClearButton();

		this.searchSwitcher = this.state.noSearchSwitcher ? null : this.getSearchSwitcher();
		const submitButton = this.state.noSearchButton ? "" : this.renderSubmitButton();

		this.searchControl = $("<div>")
			.addClass("rup-global-search__control")
			.append(submitButton)
			.append(this.searchSwitcher?.render())
			.append(searchBox)
			.append(this.clearButton);

		this.searchForm = $("<div>")
			.addClass("rup-global-search__form");

		if (this.hideSearchOptions) {
			this.searchForm.addClass("no-search-options");
		}

		this.searchForm.append(this.searchControl);

		return this.searchForm;
	}

	private getSearchBox(): SearchBox {
		return new SearchBox({
			state: this.state,
			action: () => {
				this.toggleClearButton();
				clearTimeout(this.lookupTimer);
				this.lookupTimer = window.setTimeout(() => this.updateSearchOptions(), 50);
			},
			onKeyDown: this.onKeyDown.bind(this)
		});
	}

	private setContainerActive(isActive: boolean) {
		this.searchContainer?.toggleClass("rup-global-search__container--active", isActive);
	}

	private onKeyDown(e: JQueryKeyEventObject) {
		const code = e.key;
		if (code === KeyCode.Enter) {
			this.search();
		}
		else if (code === KeyCode.ArrowDown) {
			if (this.selectOption(1)) {
				return;
			}
		}
		else if (code === KeyCode.ArrowUp) {
			if (this.selectOption(-1)) {
				return;
			}
		}
	}

	private selectOption(direction: number): boolean {
		const optionButtonClass = "rup-global-search__selectable-option--active globalSearch__button--active globalSearch__button";
		const current = this.searchBox.selectedOption;
		if (current) {
			const index = this.searchOptions.options.indexOf(current);
			const prevOrNext = this.searchOptions.options[index + direction];
			if (prevOrNext) {
				current.toggleClass(optionButtonClass);
				prevOrNext.toggleClass(optionButtonClass);
				this.searchBox.selectedOption = prevOrNext;
			}
			return true;
		}

		const first = this.searchOptions.options[0];
		first?.toggleClass(optionButtonClass);
		this.searchBox.selectedOption = first;

		return false;
	}

	private getSearchBoxPresentation(): SearchBoxPresentation {
		return new SearchBoxPresentation({
			state: this.state,
			action: (event) => this.clickContainer(event)
		});
	}

	private toggleClearButton() {
		(this.searchBox.needClear || this.isPresentationMode) && 
			this.container.has(":focus").length ? this.clearButton.show() : this.clearButton.hide();
	}

	private renderClearButton(): JQuery {
		return $("<button>")
			.addClass("rup-icon-btn rup-icon-btn--sm rup-icon-btn--neutral rup-global-search__clear-button globalSearch__clear")
			.css("display", "inline-flex")
			.append(RupIcons.clear)
			.attr({
				title: "Clear",
				type: "button",
				"data-test-id": "search-clear-btn"
			})
			.click((event) => {
				event.preventDefault();
				this.clear();
			});
	}

	public clear(): void {
		this.state.contentSearchBlockList = [];
		this.isPresentationMode = false;
		this.removeSearchOptions();
		this.searchContainer.remove();
		this.container.append(this.renderSearchContainer());

		this.state.handlers.clearResult?.call(this);
	}

	private updateSearchOptions(): void {
		const skipSearchOptions = this.isPresentationMode ||
			this.hideSearchOptions ||
			(this.searchMode !== GlobalSearchMode.SemanticSearch && !this.state.lookupsAlways);

		if (skipSearchOptions) {
			return;
		}

		this.searchOptions?.updateSearchOptions(this.searchBox.text);

		this.searchContainer.addClass("rup-global-search__container--expanded rup-elevation");
	}

	private focusOutControl(event: JQueryKeyEventObject): void {
		if (!(this.container.has(event.target).length || 
			this.searchOptionsContainer?.has(event.target).length || 
			this.container.has(":focus").length ||
			Utils.isGlobalSearchDialogEvent(event))) {
			this.focusOut();
		} else {
			this.setContainerActive(true);
		}
	}

	private focusOut(): void {
		this.searchBox.addTextBlockIfNecessary();
		this.removeSearchOptions();
		if (this.state.searchMode !== GlobalSearchMode.Default && this.getSearchBlocks().length) {
			this.isPresentationMode = true;
			this.state.contentSearchBlockList = this.searchBox.terms;
			this.rerenderControl();
		}
		if (this.searchBox && this.searchBox.isEmpty) {
			this.searchBox.turnOnEmptySearchBoxPlaceholderText();
		}
		this.searchBox.inputField?.blur();
		this.clearButton.hide();
		this.setContainerActive(!this.searchBox.isEmpty);
	}

	private getDocuments(): JQuery {
		return getDocumentIFrames(top.document);
	}

	private removeSearchOptions(): void {
		clearTimeout(this.lookupTimer);
		this.getDocuments().off("click", this.focusOutControl);

		if (this.searchContainer) {
			this.searchContainer.removeClass("rup-global-search__container--expanded rup-elevation");
		}
		if (this.searchOptionsContainer) {
			this.searchOptionsContainer.remove();
			this.searchOptionsContainer = null;
		}
		if (this.searchOptions) {
			this.searchOptions.dispose();
			this.searchOptions = null;
		}
	}

	private renderSubmitButton(): JQuery {
		return $("<button>")
			.addClass("rup-icon-btn rup-icon-btn--sm rup-icon-btn--neutral rup-global-search__search-button globalSearch__button")
			.append(RupIcons.search)
			.attr({
				title: "Search",
				type: "button",
				"data-test-id": "search-submit-btn"
			})
			.click((e) => {
				e.preventDefault();
				e.stopPropagation();
				this.search();
			});
	}

	public search(): void {
		if (!this.validate()) {
			return;
		}
		this.focusOut();
		this.removeSearchOptions();

		gaLogAction?.(
			`globalsearch:${Utils.getAnalyticSearchMode(this.state)}`,
			`globalsearch:${GlobalSearchScopeName[this.state.searchScope]}`,
			'search phrase',
			this.searchBox.searchPhraseLog);

		this.state.handlers.executeSearch();
	}

	public validate(): boolean {
		if (!this.state.allowEmptySearch && !this.validateEmpty()) {
			alert(Labels.EmptySearchMessage);
			return false;
		}

		if (!this.validateSearch()) {
			alert(Labels.InvalidSearchSyntaxMessage);
			return false;
		}

		return true;
	}

	private validateEmpty(): boolean {
		const blocks = this.getSearchBlocks();
		return blocks.length > 0;
	}

	private validateSearch(): boolean {
		const blocks = this.searchBox.terms;
		let logicalExpression = "";
		blocks.forEach(block => {
			if (block.type === SearchBlockType.Operator) {
				const operator = Utils.searchBlockOperators[block.opType];
				logicalExpression += operator.jsEquivalent;
			}
			else {
				logicalExpression += "true";
			}
		});

		// Implying AND for NOT operator.
		logicalExpression = logicalExpression.replace(/true!/g, "true&&!").replace(/\)!/g, ")&&!");

		try {
			eval(logicalExpression);
		}
		catch (err) {
			return false;
		}

		return true;
	}

	private getSearchSwitcher(): SearchSwitcher {
		return new SearchSwitcher(this.state);
	}
}

RFE.GlobalSearch = GlobalSearch;
